import { IFortniteLeaderboardBlock } from 'services/app/models/ILandingPageContent';
import { getDefaultEditorState, getDefaultTextNode } from 'components/ui/RichTextEditorV3/getDefaultEditorState';

export type IBlockRegion = 'landingPageBlock' | 'panel';
export type GameType = 'battle-royale' | 'zero-build';
export type LeaderboardType = 'general' | 'ranked';

export interface IFortniteLeaderboardBlockProps {
  index?: number;
  item: IFortniteLeaderboardBlock;
  onEdit: (item: IFortniteLeaderboardBlock) => void;
  onOpenBlockSettings?: () => void;
  onRemove: () => void;
  region: IBlockRegion;
}

export interface IFortniteLeaderboardBlockData {
  background: {
    image: {
      desktop: string;
      mobile: string;
    };
  }
  defaultFilters: {
    gameType: GameType;
    leaderboardType: LeaderboardType;
    season: string;
    session: string;
    tournament: string;
  }
  description: {
    rawData: string;
    show: boolean;
  };
  title: {
    rawData: string;
    show: boolean;
  }
}

export const DEFAULT_FORTNITE_LEADERBOARD: IFortniteLeaderboardBlockData = {
  background: {
    image: {
      desktop: '',
      mobile: '',
    },
  },
  title: {
    rawData: getDefaultEditorState([
      getDefaultTextNode({
        text: 'Lorem ipsum dolor sit amet',
        textFormatType: 'bold',
        fontSize: 60,
      }),
    ]),
    show: true,
  },
  defaultFilters: {
    leaderboardType: 'general',
    season: '',
    session: '',
    tournament: '',
    gameType: 'battle-royale',
  },
  description: {
    rawData: getDefaultEditorState([
      getDefaultTextNode({
        text: 'Lorem ipsum dolor sit amet consectetur. Sed nisl lectus odio ac vitae ornare integer. Vel sit quam ac semper pellentesque. Ullamcorper justo vulputate ultricies volutpat nisl neque tincidunt. Hendrerit habitant lorem proin sed.',
        fontSize: 20,
      }),
    ]),
    show: true,
  },
};

export const getLastUpdatedDateString = (timestamp: number): string => {
  // Format the date part (month/day/year)
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
  const dateString = dateFormatter.format(timestamp);

  // Format the time part (hours:minutes AM/PM)
  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  const timeString = timeFormatter.format(timestamp);

  // Get the timezone offset in hours and minutes
  const offset = -new Date(timestamp).getTimezoneOffset(); // in minutes
  const offsetHours = Math.floor(offset / 60);
  const offsetMinutes = offset % 60;
  const offsetSign = offset >= 0 ? '+' : '-';
  const formattedOffset = `GMT${offsetSign}${Math.abs(offsetHours).toString().padStart(2, '0')}${Math.abs(offsetMinutes).toString().padStart(2, '0')}`;

  // Combine the formatted date, time, and timezone offset
  const formattedString = `${dateString}, ${timeString} ${formattedOffset}`;

  return formattedString;
};

export const SEASON_MAP = {
  1: 'One',
  2: 'Two',
  3: 'Three',
  4: 'Four',
  5: 'Five',
  6: 'Six',
  7: 'Seven',
  8: 'Eight',
  9: 'Nine',
  10: 'Season X',
  11: 'Chapter 2 Season One',
  12: 'Chapter 2 Season Two',
  13: 'Chapter 2 Season Three',
  14: 'Chapter 2 Season Four',
  15: 'Chapter 2 Season Five',
  16: 'Chapter 2 Season Six',
  17: 'Chapter 2 Season Seven',
  18: 'Chapter 2 Season Eight',
  19: 'Chapter 3 Season 1',
  20: 'Chapter 3 Season 2',
  21: 'Chapter 3 Season 3',
  22: 'Chapter 3 Season 4',
  23: 'Chapter 4 Season 1',
  24: 'Chapter 4 Season 2',
  25: 'Chapter 4 Season 3',
  26: 'Chapter 4 Season 4',
  27: 'Fortnite OG (Chapter 5 Season 5)',
  28: 'Chapter 5 Season 1',
  29: 'Chapter 5 Season 2',
  // TODO: CHECK ON THIS. SENSITIVE INFO (FANS DO NOT KNOW)
  // 30: 'Chapter 5 Season 3',
  // 31: 'Chapter 5 Season 4',
} as const;

export const SEASON_DROPDOWN_OPTIONS = Object.entries(SEASON_MAP)
.sort(([keyA], [keyB]) => Number(keyB) - Number(keyA))
.map(([key, value]) => ({
  label: value,
  value: Number(key),
}));

export type SeasonType = keyof typeof SEASON_MAP;
export type Season = typeof SEASON_MAP[SeasonType];
